import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useAuthenticator } from "@aws-amplify/ui-react";
import * as Sentry from "@sentry/browser";
import { fetchUserAttributes } from 'aws-amplify/auth';
import { authClient } from "../utils/amplifyClient";
import { enforceMigrationIssuerGate } from "../utils/migrationIssuerGate";
import {
  companyOwnersByCognitoId,
  getCompany,
  listBusinessAdmins,
  listCompanies,
  listCompanyByCompanyOwnerId,
  listCompanyOwners,
  listUsers,
  usersByCognitoId,
  usersByCompanyId,
} from "../graphql/queries";
import { updateBusinessAdmin as updateBusinessAdminMutation, updateCompanyOwner as updateCompanyOwnerMutation, updateUser as updateUserMutation } from "../graphql/mutations";
import { Company, CompanyOwner, User, BusinessAdmin } from "../types/schema";
import { iProvider, UserContextValue } from "../types";
import { loadCompanyUserDirectory } from "../utils/companyUserDirectory";
import {
  clearFreeDemoSignup,
  ensureFreeDemoCompanyOwner,
  isFreeDemoSignupPending,
} from "../utils/createFreeDemoOwner";
import { ensureUserForCompanyOwner } from "../utils/ensureUserForCompanyOwner";
import {
  findMatchingInviteUser,
  isArchivedCompany,
  isMemberOfCompany,
  isUnlinkedInvite,
  liveCompanyNameFromArchive,
  mergeCompaniesById,
  mergeRecordsById,
  pickPreferredCompany,
  pickRecordForCompany,
} from "../utils/preferredCompany";

// TODO: Add a check here to see if the resource library isn't properly generated and then create one -- this should be able to be removed eventually, but potentially a good fail safe to have and less duplication than having it in the resource library section.  


// TODO: Add a check to see if logged in user is company Owner, or a user with admin permissions to mesh sensitive user information with the users.  If they just have manage users permissions at this point I won't let them see or edit/update sensitive info -- they'll just see placeholders.
// TODO: make a combined user type that combines both the user and their sensative info if permissions allow that can just return the basic user if no permissions. 

export const UserContext = createContext<UserContextValue>({
  businessAdmin: null,
  company: null,
  allCompanyUsers: [],
  companyUser: null,
  companyOwner: null,
  getUserDetails: async () => { },
  isAccountDeactivated: false,
  isLoading: false,
  noNavBar: false,
  setAllCompanyUsers: () => { },
  setBusinessAdmin: () => { },
  setCompany: () => { },
  setCompanyUser: () => { },
  setCompanyOwner: () => { },
  setNoNavBar: () => { },
  setShouldUpdateUser: () => { },
  shouldUpdateUser: false,
  trainingModuleEnrollment: false,
  skillTrackingModuleEnrollment: false,
});

// eslint-disable-next-line react-refresh/only-export-components
export const useUserContext = () => useContext(UserContext);

export const UserProvider = ({ children }: iProvider) => {
  const [allCompanyUsers, setAllCompanyUsers] = useState<User[] | []>([])
  const [businessAdmin, setBusinessAdmin] = useState<BusinessAdmin | null>(null);
  const [companyUser, setCompanyUser] = useState<User | null>(null);
  const [companyOwner, setCompanyOwner] = useState<CompanyOwner | null>(null)
  const [company, setCompany] = useState<Company | null>(null);
  const [isAccountDeactivated, setIsAccountDeactivated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [noNavBar, setNoNavBar] = useState(false);
  const [shouldUpdateUser, setShouldUpdateUser] = useState(false);
  const [trainingModuleEnrollment, skillTrackingModuleEnrollment] = [
    companyUser?.trainingModuleEnrollment || false,
    companyUser?.skillTrackingModuleEnrollment || false,
  ];
  const { user } = useAuthenticator((context) => {
    return [context.user];
  });
  /** After first successful profile hydrate, keep last-known identity during soft refresh
   *  (Cognito token restore on Mac lock/unlock) so in-progress pages are not unmounted. */
  const hasHydratedProfileRef = useRef(false);

  const getUserDetails = async () => {
    setIsLoading(true);

    // ── Migration issuer-gate ──────────────────────────────────────────────
    // If the active session was issued by an OLD Cognito pool while the app is
    // configured to use a NEW Gen2 pool, force re-auth so the UserMigration
    // trigger runs. This is guarded to be inert whenever the frontend targets
    // an old pool (rollback), so it cannot cause the Aug 2026 lockout again.
    const cleared = await enforceMigrationIssuerGate();
    if (cleared) {
      setIsLoading(false);
      return;
    }

    if (!hasHydratedProfileRef.current) {
      setBusinessAdmin(null);
      setCompanyUser(null);
      setCompanyOwner(null);
      setCompany(null);
      setAllCompanyUsers([]);
    }
    try {
      // Step 1: Check BusinessAdmin table first
      try {
        const { data: baItems } = await authClient.models.BusinessAdmin.businessAdminsByCognitoId({
          cognitoId: user.userId,
        });

        if (baItems && baItems.length > 0) {
          setBusinessAdmin(baItems[0]);
          hasHydratedProfileRef.current = true;
          return;
        }
      } catch (baError) {
        console.log("BusinessAdmin lookup failed, falling through to CompanyOwner check", baError);
        Sentry.captureException(baError);
      }

      // Step 2: Resolve User and/or CompanyOwner by cognitoId.
      // Load both when dual identity exists so Owner bypass (PDF Owner parity) is not lost
      // if a User row is present with a non–super-admin role.
      // Same Cognito identity can have rows on both a live company and an archived copy —
      // never take items[0] as source of truth.
      const userResponse = (await authClient.graphql({
        query: usersByCognitoId,
        variables: { cognitoId: user.userId, limit: 50 },
      })) as { data?: { usersByCognitoId?: { items?: (User | null)[] } } };
      let userItems = (userResponse.data?.usersByCognitoId?.items || []).filter(
        (item): item is User => Boolean(item?.id),
      );

      const ownerResponse = (await authClient.graphql({
        query: companyOwnersByCognitoId,
        variables: { cognitoId: user.userId, limit: 50 },
      })) as { data?: { companyOwnersByCognitoId?: { items?: (CompanyOwner | null)[] } } };
      const ownerItems = (ownerResponse.data?.companyOwnersByCognitoId?.items || []).filter(
        (item): item is CompanyOwner => Boolean(item?.id),
      );

      let loginEmail: string | undefined;
      try {
        const attributes = await fetchUserAttributes();
        loginEmail = attributes.email?.trim().toLowerCase() || undefined;
      } catch (attrError) {
        Sentry.captureException(attrError);
      }

      // Pending invites have no cognitoId ("Resend Invite"), so they never show up in
      // usersByCognitoId. Include email-matched User rows so we can land on the live company.
      if (loginEmail) {
        try {
          const byEmail = (await authClient.graphql({
            query: listUsers,
            variables: { filter: { email: { eq: loginEmail } }, limit: 1000 },
          })) as { data?: { listUsers?: { items?: (User | null)[] } } };
          let emailItems = byEmail.data?.listUsers?.items || [];
          if (!emailItems.some((item) => item?.email?.trim().toLowerCase() === loginEmail)) {
            const retry = (await authClient.graphql({
              query: listUsers,
              variables: { limit: 1000 },
            })) as { data?: { listUsers?: { items?: (User | null)[] } } };
            emailItems = retry.data?.listUsers?.items || [];
          }
          userItems = mergeRecordsById([
            userItems,
            emailItems.filter(
              (item): item is User =>
                Boolean(item?.id) && item?.email?.trim().toLowerCase() === loginEmail,
            ),
          ]);
        } catch (emailUsersError) {
          Sentry.captureException(emailUsersError);
        }
      }

      let companyData: Company | null = null;
      let usersData: User[] = [];

      const candidateIds = [
        ...new Set(
          [...userItems, ...ownerItems]
            .map((item) => item.companyId)
            .filter((id): id is string => Boolean(id)),
        ),
      ];

      const companiesById: Company[] = [];
      for (const id of candidateIds) {
        try {
          const companyResult = (await authClient.graphql({
            query: getCompany,
            variables: { id },
          })) as { data?: { getCompany?: Company | null } };
          if (companyResult.data?.getCompany?.id) {
            companiesById.push(companyResult.data.getCompany);
          }
        } catch (companyError) {
          Sentry.captureException(companyError);
        }
      }

      const companiesByOwner: Company[] = [];
      for (const owner of ownerItems) {
        try {
          const byOwner = (await authClient.graphql({
            query: listCompanyByCompanyOwnerId,
            variables: { companyOwnerId: owner.id, limit: 50 },
          })) as {
            data?: { listCompanyByCompanyOwnerId?: { items?: (Company | null)[] } };
          };
          companiesByOwner.push(
            ...(byOwner.data?.listCompanyByCompanyOwnerId?.items || []).filter(
              (item): item is Company => Boolean(item?.id),
            ),
          );
        } catch (byOwnerError) {
          Sentry.captureException(byOwnerError);
        }
      }

      let candidates = mergeCompaniesById([companiesById, companiesByOwner]);
      let preferred = pickPreferredCompany(candidates);

      // Archived copy often has a different companyOwnerId than the live sibling.
      // If we still only have the archive, look up the live name (e.g. SoloDevelop)
      // and attach the pending invite User on that company.
      if (preferred && isArchivedCompany(preferred)) {
        const liveName = liveCompanyNameFromArchive(preferred.name);
        if (liveName) {
          try {
            const named = (await authClient.graphql({
              query: listCompanies,
              variables: { filter: { name: { eq: liveName } }, limit: 20 },
            })) as { data?: { listCompanies?: { items?: (Company | null)[] } } };
            const siblings = (named.data?.listCompanies?.items || []).filter(
              (item): item is Company => Boolean(item?.id) && !isArchivedCompany(item),
            );
            const accepted: Company[] = [];
            for (const sibling of siblings) {
              let inviteUser: User | null = findMatchingInviteUser(userItems, {
                email: loginEmail,
                name: userItems[0]?.name,
              });
              if (!inviteUser || inviteUser.companyId !== sibling.id) {
                const directory = (await authClient.graphql({
                  query: usersByCompanyId,
                  variables: { companyId: sibling.id, limit: 1000 },
                })) as { data?: { usersByCompanyId?: { items?: (User | null)[] } } };
                inviteUser = findMatchingInviteUser(directory.data?.usersByCompanyId?.items || [], {
                  email: loginEmail,
                  name: userItems[0]?.name,
                });
                if (inviteUser) {
                  userItems = mergeRecordsById([userItems, [inviteUser]]);
                }
              }
              if (inviteUser?.companyId === sibling.id || isMemberOfCompany(sibling, userItems, ownerItems)) {
                accepted.push(sibling);
              }
            }
            candidates = mergeCompaniesById([candidates, accepted]);
            preferred = pickPreferredCompany(candidates);
          } catch (namedError) {
            Sentry.captureException(namedError);
          }
        }
      }

      companyData = preferred;
      let companyId = preferred?.id;
      let ownerData = pickRecordForCompany(
        ownerItems,
        companyId,
        (item) => item.id === preferred?.companyOwnerId,
      );
      let userData = pickRecordForCompany(userItems, companyId);

      // Link this Cognito identity to the resolved User row when needed:
      //  - pending invite (empty/pending- cognitoId), or
      //  - MIGRATED user: the row was resolved by email but still holds a different
      //    (old-pool) cognitoId. This runs here (not only in the no-record fallback)
      //    because the email-inclusion lookup above populates userData for migrated
      //    users, so the deactivated-branch relink is never reached for them.
      const userNeedsRelink =
        userData?.id &&
        (isUnlinkedInvite(userData.cognitoId) || userData.cognitoId !== user.userId);
      if (userNeedsRelink) {
        try {
          const linked = (await authClient.graphql({
            query: updateUserMutation,
            variables: { input: { id: userData!.id, cognitoId: user.userId } },
          })) as { data?: { updateUser?: User | null } };
          userData = linked.data?.updateUser ?? { ...userData!, cognitoId: user.userId };
          console.log(`[UserContext] User ${userData!.id} cognitoId set to ${user.userId}`);
        } catch (linkInviteError) {
          console.error("Failed to link User row to Cognito user:", linkInviteError);
          Sentry.captureException(linkInviteError);
        }
      }

      // Same relink for a CompanyOwner resolved by email with a stale/pending cognitoId.
      if (ownerData?.id && (isUnlinkedInvite(ownerData.cognitoId) || ownerData.cognitoId !== user.userId)) {
        try {
          const linkedOwner = (await authClient.graphql({
            query: updateCompanyOwnerMutation,
            variables: { input: { id: ownerData.id, cognitoId: user.userId } },
          })) as { data?: { updateCompanyOwner?: CompanyOwner | null } };
          ownerData = linkedOwner.data?.updateCompanyOwner ?? { ...ownerData, cognitoId: user.userId };
          console.log(`[UserContext] CompanyOwner ${ownerData.id} cognitoId set to ${user.userId}`);
        } catch (linkOwnerError) {
          console.error("Failed to link CompanyOwner row to Cognito user:", linkOwnerError);
          Sentry.captureException(linkOwnerError);
        }
      }

      // Owners need a real User row for Daily Brief and other user-scoped features.
      // Skip ensure when User already has companyId + cognitoId (happy path).
      if (ownerData?.id && (!userData?.companyId || !userData?.cognitoId)) {
        try {
          userData = await ensureUserForCompanyOwner({
            ...ownerData,
            companyId: companyId || ownerData.companyId,
            cognitoId: ownerData.cognitoId || user.userId,
          });
          if (!companyId && userData.companyId) {
            companyId = userData.companyId;
          }
        } catch (ensureError) {
          console.error("Failed to ensure User for CompanyOwner:", ensureError);
          Sentry.captureException(ensureError);
        }
      }

      if (companyId) {
        try {
          usersData = await loadCompanyUserDirectory(companyId, companyData?.companyOwnerId);
        } catch (directoryError) {
          Sentry.captureException(directoryError);
          const { data } = await authClient.models.User.usersByCompanyId({ companyId });
          usersData = data;
        }
      }

      setCompanyUser((prev) => userData ?? (hasHydratedProfileRef.current ? prev : null));
      setCompanyOwner((prev) => ownerData ?? (hasHydratedProfileRef.current ? prev : null));
      setCompany((prev) => companyData ?? (hasHydratedProfileRef.current ? prev : null));
      if (userData || ownerData || companyData) {
        hasHydratedProfileRef.current = true;
      }

      // If no record found by cognitoId, attempt to link by email before declaring deactivated.
      // This handles the case where a BusinessAdmin, CompanyOwner, or User was pre-created
      // with a pending/empty cognitoId and the user is signing in for the first time.
      if (!userData && !ownerData && !companyData) {
        // Soft refresh glitch: keep last-known profile rather than flipping to deactivated.
        if (hasHydratedProfileRef.current) {
          setIsAccountDeactivated(false);
          return;
        }

        const linked = await attemptLinkByCognitoId(user.userId);
        if (linked) {
          // Successfully linked — re-run getUserDetails to load the full context.
          // Don't let finally set isLoading=false (the recursive call manages its own loading state).
          getUserDetails();
          return;
        }

        // Free-demo self-serve signup: create CompanyOwner here so App does not
        // unmount /signup (deactivated gate) before Login can finish creation.
        if (isFreeDemoSignupPending()) {
          try {
            const owner = await ensureFreeDemoCompanyOwner(user.userId);
            clearFreeDemoSignup();
            setCompanyOwner(owner);
            setIsAccountDeactivated(false);
            hasHydratedProfileRef.current = true;
            setIsLoading(false);
            return;
          } catch (freeDemoError) {
            console.error('Free-demo CompanyOwner creation failed:', freeDemoError);
            Sentry.captureException(freeDemoError);
          }
        }

        setIsAccountDeactivated(true);
        setIsLoading(false);
        return;
      }
      setIsAccountDeactivated(false);

      setAllCompanyUsers(usersData);
    } catch (error) {
      console.log("error", error);
      Sentry.captureException(error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!user) {
      hasHydratedProfileRef.current = false;
      setIsLoading(false);
      setIsAccountDeactivated(false);
      setBusinessAdmin(null);
      setCompanyUser(null);
      setCompanyOwner(null);
      setCompany(null);
      setAllCompanyUsers([]);
      return;
    }
    getUserDetails();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  /**
   * Attempts to link a pre-created record (BusinessAdmin, CompanyOwner, or User) to this
   * Cognito user by matching on email. Records created from admin tables get a placeholder
   * cognitoId (e.g. "pending-12345") or no cognitoId. This function finds those records
   * and writes the real cognitoId so future lookups work via the GSI.
   *
   * Also handles CROSS-ACCOUNT MIGRATION: when a legacy user is migrated into the new
   * Cognito pool they receive a brand-new sub (cognitoId). Their existing DynamoDB record
   * still holds the OLD pool's cognitoId — a real, populated value (not "pending-"). Since
   * this function only runs after a lookup by the current sub returned nothing, an
   * email-matched record whose stored cognitoId differs from the current sub is a migrated
   * user, and we overwrite it with the new sub.
   */
  const attemptLinkByCognitoId = async (cognitoId: string): Promise<boolean> => {
    try {
      const attributes = await fetchUserAttributes();
      const email = attributes.email?.toLowerCase();
      if (!email) return false;

      // A record is relinkable to this authenticated session when its email matches AND
      // either (a) it has no real cognitoId yet (pending invite), or (b) it has a stale
      // cognitoId that differs from the current sub (migrated user from the old pool).
      // Safe because this only runs when lookup by the current sub already found nothing.
      const isRelinkable = (item: { email?: string | null; cognitoId?: string | null } | null): boolean => {
        if (!item || item.email?.toLowerCase() !== email) return false;
        const existing = item.cognitoId;
        if (!existing || existing === "" || existing.startsWith("pending-")) return true;
        return existing !== cognitoId; // stale sub → migrated user
      };

      // Try BusinessAdmin first
      // Note: We skip the DynamoDB email filter and match in JS to handle case mismatches.
      // This only runs once per user (on first sign-in), so the scan cost is acceptable.
      const baResponse = await authClient.graphql({
        query: listBusinessAdmins,
        variables: { limit: 1000 },
      });
      const baMatches = baResponse.data.listBusinessAdmins?.items?.filter(isRelinkable);
      if (baMatches && baMatches.length > 0) {
        const record = baMatches[0]!;
        await authClient.graphql({
          query: updateBusinessAdminMutation,
          variables: { input: { id: record.id, cognitoId } },
        });
        console.log(`[UserContext] BusinessAdmin ${record.id} linked to cognitoId ${cognitoId}`);
        return true;
      }

      // Try CompanyOwner
      const ownerResponse = await authClient.graphql({
        query: listCompanyOwners,
        variables: { limit: 1000 },
      });
      const ownerMatches = ownerResponse.data.listCompanyOwners?.items?.filter(isRelinkable);
      if (ownerMatches && ownerMatches.length > 0) {
        const record = ownerMatches[0]!;
        await authClient.graphql({
          query: updateCompanyOwnerMutation,
          variables: { input: { id: record.id, cognitoId } },
        });
        console.log(`[UserContext] CompanyOwner ${record.id} linked to cognitoId ${cognitoId}`);
        return true;
      }

      // Try User — use email filter here since User table can be large,
      // but also do a case-insensitive JS check as a safety net
      const userResponse = await authClient.graphql({
        query: listUsers,
        variables: { filter: { email: { eq: email } }, limit: 1000 },
      });
      let userMatches = userResponse.data.listUsers?.items?.filter(isRelinkable);

      // If the eq filter missed due to case mismatch, retry without filter
      if ((!userMatches || userMatches.length === 0)) {
        const retryResponse = await authClient.graphql({
          query: listUsers,
          variables: { limit: 1000 },
        });
        userMatches = retryResponse.data.listUsers?.items?.filter(isRelinkable);
      }

      if (userMatches && userMatches.length > 0) {
        const record = userMatches[0]!;
        await authClient.graphql({
          query: updateUserMutation,
          variables: { input: { id: record.id, cognitoId } },
        });
        console.log(`[UserContext] User ${record.id} linked to cognitoId ${cognitoId}`);
        return true;
      }

      return false;
    } catch (error) {
      console.error("[UserContext] attemptLinkByCognitoId failed:", error);
      Sentry.captureException(error);
      return false;
    }
  };

  useEffect(() => {
    if (shouldUpdateUser && companyUser) {
      const updateUser = async () => {
        await authClient.models.User.update({
          id: companyUser.id,
        });
        setShouldUpdateUser(false);
        setCompanyUser({
          ...companyUser,
        })
      };
      updateUser();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldUpdateUser, companyUser]);


  const value = {
    businessAdmin,
    company,
    allCompanyUsers,
    companyUser,
    companyOwner,
    getUserDetails,
    isAccountDeactivated,
    isLoading,
    noNavBar,
    setAllCompanyUsers,
    setBusinessAdmin,
    setCompany,
    setCompanyUser,
    setCompanyOwner,
    setNoNavBar,
    setShouldUpdateUser,
    shouldUpdateUser,
    trainingModuleEnrollment,
    skillTrackingModuleEnrollment,
  };

  return <UserContext.Provider value={value}>{children}</UserContext.Provider>;
};
